import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Spin } from "antd";
import { ArrowLeft, ShieldAlert, ShieldCheck, ShieldQuestion } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { PageShell } from "../../../design-system/tableUi";
import { CopyButton } from "../../../pages/enduser/ssh-proxy/sshProxyUi";
import { decodeBase64Content, getRecordingContent } from "../../../service/sshProxyService";
import CastPlayer from "./CastPlayer";
import IntegrityPanel from "./IntegrityPanel";

// One session recording on its own page, so the console gets the whole
// viewport instead of a modal's box. Reached from the recordings list and a
// gateway's Sessions tab: /pam/jumpServer-sessionRecordings/play/:id, where id is the
// recording's primary key (what getRecordingContent takes).
//
// The list row may ride along in location.state — only for older Guacamole
// rows, whose bytes live behind a presigned recordingUrl rather than the API.

const INTEGRITY = {
  intact: { Icon: ShieldCheck, label: "Integrity intact", fg: "var(--an-successFg)", bg: "var(--an-successBg)", bd: "var(--an-successBd)" },
  altered: { Icon: ShieldAlert, label: "Integrity: altered", fg: "var(--an-dangerFg)", bg: "var(--an-dangerBg)", bd: "var(--an-dangerBd)" },
  unknown: { Icon: ShieldQuestion, label: "Integrity unknown", fg: "var(--an-warnFg)", bg: "var(--an-warnBg)", bd: "var(--an-warnBd)" },
};

const looksLikeCast = (text) => {
  const first = (text || "").split("\n", 1)[0];
  try {
    return JSON.parse(first)?.version === 2;
  } catch {
    return false;
  }
};

export default function RecordingPlayerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const row = location.state?.row || null;

  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [text, setText] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    getRecordingContent({
      id: Number(id) || id,
      onSuccess: (data) => {
        setLoading(false);
        setContent(data || null);
        setText(decodeBase64Content(data?.content));
        if (!data?.content && !row?.recordingUrl) setError("This recording has no stored content.");
      },
      onFailure: () => {
        setLoading(false);
        if (!row?.recordingUrl) setError("Couldn't load this recording.");
      },
    });
  }, [id, row?.recordingUrl]);

  const back = () => {
    // Back to wherever Play was clicked; a direct visit has no history entry.
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate("/pam/jumpServer-sessionRecordings");
  };

  const mime = content?.recordingMimeType || row?.recordingMimeType;
  const isCast = mime ? mime === "asciicast" : looksLikeCast(text);
  const sessionId = content?.sessionId || row?.sessionId;
  const integrity = INTEGRITY[content?.integrity] || (content ? INTEGRITY.unknown : null);

  return (
    <PageShell style={{ paddingTop: 16, paddingBottom: 16 }}>
      {/* Compact header: everything above the terminal costs it height. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12, fontFamily: AN_FONT }}>
        <Button onClick={back} icon={<ArrowLeft size={14} style={{ verticalAlign: -2 }} />}>
          Back
        </Button>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: AN_COLORS.ink, letterSpacing: -0.3 }}>Session recording</h1>
        {sessionId && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontFamily: AN_MONO, fontSize: 12.5, color: AN_COLORS.muted }}>{sessionId}</span>
            <CopyButton text={sessionId} label="" />
          </span>
        )}
        {integrity && (
          <span
            title="Details below the player"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 999,
              background: integrity.bg, border: `1px solid ${integrity.bd}`, color: integrity.fg, fontSize: 12, fontWeight: 500,
            }}
          >
            <integrity.Icon size={13} />
            {integrity.label}
          </span>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: "center" }}>
          <Spin />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {/* An altered recording must be seen before anything is played. */}
          {content?.integrity === "altered" && <IntegrityPanel content={content} />}

          {error && (
            <div style={{ padding: 40, textAlign: "center", color: AN_COLORS.muted, fontFamily: AN_FONT, fontSize: 13 }}>{error}</div>
          )}

          {text && isCast && <CastPlayer text={text} />}

          {text && !isCast && (
            <pre
              style={{
                margin: 0, height: "calc(100vh - 220px)", minHeight: 260, overflow: "auto", background: "#0B0D12",
                color: "#E6E8EC", borderRadius: 8, padding: "12px 14px", fontFamily: AN_MONO, fontSize: 12, lineHeight: 1.55,
              }}
            >
              {text}
            </pre>
          )}

          {!text && row?.recordingUrl && (
            <object
              type="text/html"
              data={row.recordingUrl}
              width="100%"
              style={{ height: "calc(100vh - 220px)", minHeight: 260, background: "var(--an-surface)", borderRadius: 8 }}
              aria-label="Session recording"
            />
          )}

          {content && content.integrity !== "altered" && <IntegrityPanel content={content} />}
        </div>
      )}
    </PageShell>
  );
}
